import { useCallback, useEffect, useState } from "react";
import axios from "axios";

import { PhaseProgressWidget } from "./overview/PhaseProgressWidget";
import { CheckinChartWidget } from "./overview/CheckinChartWidget";
import { MeasurementsChartWidget } from "./overview/MeasurementsChartWidget";
import { TopProgressionsWidget } from "./overview/TopProgressionsWidget";
import { CompletedWorkoutsWidget } from "./overview/CompletedWorkoutsWidget";
import { LastActivityWidget } from "./overview/LastActivityWidget";
import { CoachNoteWidget } from "./overview/CoachNoteWidget";
import { RecentBadgesWidget } from "./overview/RecentBadgesWidget";
import { ClientAnniversaryWidget } from "./overview/ClientAnniversaryWidget";
import { WeeklyVolumeByMuscleWidget } from "./overview/WeeklyVolumeByMuscleWidget";
import { AIProgressAnalysisWidget } from "./overview/AIProgressAnalysisWidget";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Panoramica cliente — landing tab della pagina cliente.
 *
 * Layout: griglia a 2 colonne (1 su mobile) con widget ordinati come
 * richiesto: 5, 1, 2, 4, 3 + extra. La pagina aggrega dati di più endpoint
 * con Promise.all per ridurre il TTFB.
 */
export default function OverviewTab({ clientId, client }) {
  const [data, setData] = useState({
    phases: [],
    measurements: [],
    checkinConfig: null,
    checkinResponses: [],
    logs: [],
    workouts: [],
    badges: null,
    exerciseLibrary: [],
  });
  const [loading, setLoading] = useState(true);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const [phasesR, measR, ckCfgR, ckRespR, logsR, workoutsR, badgesR, exR] = await Promise.all([
        axios.get(`${API}/clients/${clientId}/phases`),
        axios.get(`${API}/clients/${clientId}/measurements`),
        axios.get(`${API}/clients/${clientId}/checkin/config`),
        axios.get(`${API}/clients/${clientId}/checkin/responses`, { params: { limit: 12 } }),
        axios.get(`${API}/clients/${clientId}/logs`),
        axios.get(`${API}/clients/${clientId}/workouts`),
        axios.get(`${API}/clients/${clientId}/badges`),
        axios.get(`${API}/exercises`),
      ]);
      setData({
        phases: phasesR.data || [],
        measurements: measR.data || [],
        checkinConfig: ckCfgR.data || null,
        checkinResponses: ckRespR.data || [],
        logs: logsR.data || [],
        workouts: workoutsR.data || [],
        badges: badgesR.data || null,
        exerciseLibrary: exR.data || [],
      });
    } catch (err) {
      console.error("[OverviewTab] fetch failed", err);
    } finally {
      setLoading(false);
    }
  }, [clientId]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16" data-testid="overview-loading">
        <div className="w-7 h-7 border-2 border-[#007AFF] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="overview-tab">
      {/* Row 1: Phase progress (full-width, sets the context) */}
      <PhaseProgressWidget phases={data.phases} />

      {/* Row 2: Check-in + Measurements side-by-side */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <CheckinChartWidget responses={data.checkinResponses} config={data.checkinConfig} />
        <MeasurementsChartWidget measurements={data.measurements} />
      </div>

      {/* Row 3: Top progressions + Completed workouts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <TopProgressionsWidget logs={data.logs} />
        <CompletedWorkoutsWidget logs={data.logs} workouts={data.workouts} />
      </div>

      {/* Row 4: Last activity + Anniversary (compact KPI row) */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <LastActivityWidget
          logs={data.logs}
          measurements={data.measurements}
          checkinResponses={data.checkinResponses}
        />
        <ClientAnniversaryWidget client={client} phases={data.phases} />
      </div>

      {/* Row 5: Recent badges + Weekly volume by muscle */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <RecentBadgesWidget badges={data.badges} />
        <WeeklyVolumeByMuscleWidget logs={data.logs} exerciseLibrary={data.exerciseLibrary} />
      </div>

      {/* Row 6: Coach quick note (full-width sticky note) */}
      <CoachNoteWidget clientId={clientId} initialNote={client?.coach_quick_note || ""} />

      {/* Row 7: AI progress analysis (Claude, on-demand) */}
      <AIProgressAnalysisWidget
        client={client}
        measurements={data.measurements}
        checkinResponses={data.checkinResponses}
        logs={data.logs}
        phases={data.phases}
        workouts={data.workouts}
      />
    </div>
  );
}
